import { Button, Card, CardBody } from '@heroui/react';
import Link from 'next/link';

import { Icon, IconType } from '../../various/Icon';

const steps = [
  {
    number: 1,
    title: 'Create a Project',
    description: 'Upload files from your computer, Drive, or Dropbox.',
    icon: 'folder',
  },
  {
    number: 2,
    title: 'Invite Collaborators',
    description: 'Invite team members or send no-signup guest links to clients.',
    icon: 'userPlus',
  },
  {
    number: 3,
    title: 'Review & Collaborate',
    description:
      'Add frame-accurate comments, compare versions side-by-side, and collaborate with your team in real-time.',
    icon: 'chat',
  },
  {
    number: 4,
    title: 'Deliver & Approve',
    description: 'Get client approvals, track deliverables, and export final assets—all in one place.',
    icon: 'checkCircle',
  },
];

export const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="px-6 py-16 backdrop-blur-lg lg:mt-8 lg:py-24">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-16">
        <div className="flex w-full flex-col gap-4">
          <h2 className="text-center font-sans text-3xl font-bold sm:text-4xl">How Does Kreatli Work?</h2>
          <div className="mx-auto max-w-2xl rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/[0.04] to-transparent px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex items-start gap-4">
              <div className="mt-0.5 hidden flex-shrink-0 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 p-2.5 sm:flex">
                <Icon icon="slides" size={20} className="text-primary" />
              </div>
              <p className="text-center text-base leading-relaxed text-foreground-500 sm:text-left sm:text-lg">
                Kreatli is a Video Collaboration &amp; Review Platform that helps video teams manage production from
                brief to delivery in one workspace. Upload files, get frame-accurate feedback, track tasks on visual
                boards, and collect client approvals — without switching between storage, review, chat, and project
                management tools.
              </p>
            </div>
          </div>
          <p className="text-center text-base font-medium text-foreground-400">Get started in 4 simple steps:</p>
        </div>
        <div className="grid w-full gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <Card
              key={index}
              className="group h-full border border-foreground-200 bg-content1 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-xl hover:shadow-primary/20"
            >
              <CardBody className="flex h-full flex-col items-center gap-6 p-6">
                <div className="relative">
                  <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10 transition-all duration-300 group-hover:scale-110 group-hover:from-primary/30 group-hover:to-primary/20">
                    <Icon
                      icon={step.icon as IconType}
                      size={28}
                      className="text-primary transition-colors duration-300"
                    />
                  </div>
                  <div className="absolute -right-2 -top-2 flex size-7 items-center justify-center rounded-full bg-foreground font-sans text-sm font-bold text-content1 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md">
                    {step.number}
                  </div>
                </div>
                <div className="flex flex-1 flex-col items-center gap-2 text-center">
                  <h3 className="font-sans text-lg font-semibold transition-colors duration-200 group-hover:text-primary">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground-500">{step.description}</p>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
        <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row">
          <Button as={Link} href="/sign-up" size="lg" className="bg-foreground text-content1">
            Explore free, no card required
          </Button>
          <Button
            as="a"
            href="https://calendar.app.google/NXbAeTAUwaBGh5x49"
            target="_blank"
            rel="noopener noreferrer nofollow"
            size="lg"
            variant="light"
          >
            Book a Demo
          </Button>
        </div>
      </div>
    </section>
  );
};
